import React, { useEffect, useState } from "react";
import { 
  Button, TextField, Paper, Grid, Snackbar, Alert, CircularProgress, 
  MenuItem, Select, InputLabel, FormControl, Autocomplete 
} from "@mui/material";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { 
  Box, Typography, LinearProgress, Checkbox, FormControlLabel, 
  Divider, Tooltip, IconButton, Card, CardContent 
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import StorageIcon from "@mui/icons-material/Storage";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import WarningIcon from "@mui/icons-material/Warning";

const StorageConfiguration = () => {
  const axiosPrivate = useAxiosPrivate();
  const [form, setForm] = useState({
    provider: "local",
    s3_bucket: "",
    s3_access_key: "",
    s3_secret_key: "",
    s3_region: "",
    azure_connection_string: "",
    azure_container: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [testing, setTesting] = useState(false);
  const [distribution, setDistribution] = useState([]);
  const [migrationStatus, setMigrationStatus] = useState({ status: "idle", progress: 0 });
  const [scanning, setScanning] = useState(false);
  const [deleteSource, setDeleteSource] = useState(false);

  const [azureContainers, setAzureContainers] = useState([]);
  const [fetchingContainers, setFetchingContainers] = useState(false);

  const fetchAzureContainers = async (connStr) => {
    const stringToUse = connStr !== undefined ? connStr : form.azure_connection_string;
    if (!stringToUse || !stringToUse.trim()) return;
    setFetchingContainers(true);
    try {
      const res = await axiosPrivate.post("/api/auth/v1/storage-config/azure-containers", {
        azure_connection_string: stringToUse
      });
      if (res.data && res.data.containers) {
        setAzureContainers(res.data.containers);
      }
    } catch (err) {
      console.error("Failed to fetch Azure containers", err);
    } finally {
      setFetchingContainers(false);
    }
  };

  const handleCreateContainer = async () => {
    if (!form.azure_connection_string || !form.azure_container) {
      setSnackbar({ open: true, message: "Connection string and container name are required", severity: "error" });
      return;
    }
    try {
      const res = await axiosPrivate.post("/api/auth/v1/storage-config/azure-containers/create", {
        azure_connection_string: form.azure_connection_string,
        azure_container: form.azure_container
      });
      setSnackbar({ open: true, message: res.data.message, severity: "success" });
      fetchAzureContainers(form.azure_connection_string);
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || "Failed to create container", severity: "error" });
    }
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validateForm = () => {
    if (form.provider === "s3") {
      if (!form.s3_bucket.trim()) return "S3 Bucket is required.";
      if (!form.s3_access_key.trim()) return "S3 Access Key is required.";
      if (!form.s3_secret_key.trim()) return "S3 Secret Key is required.";
      if (!form.s3_region.trim()) return "S3 Region is required.";
    }
    if (form.provider === "azure") {
      if (!form.azure_connection_string.trim()) return "Azure Connection String is required.";
      if (!form.azure_container.trim()) return "Azure Container name is required.";
    }
    return null;
  };

  const handleSave = async () => {
    const err = validateForm();
    if (err) { setSnackbar({ open: true, message: err, severity: "error" }); return; }
    try {
      await axiosPrivate.post("/api/auth/v1/storage-config", form);
      setSnackbar({ open: true, message: "Configuration saved successfully", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Failed to save configuration", severity: "error" });
    }
  };

  const handleTestConnection = async () => {
    const validationErr = validateForm();
    if (validationErr) { setSnackbar({ open: true, message: validationErr, severity: "error" }); return; }
    setTesting(true);
    try {
      const response = await axiosPrivate.post(
        "/api/auth/v1/storage-config/test",
        form,
      );
      setSnackbar({
        open: true,
        message: response.data.message || "Connection successful!",
        severity: "success",
      });
    } catch (err) {
      const raw = err.response?.data?.message;
      const msg =
        typeof raw === "string"
          ? raw
          : raw && typeof raw === "object"
          ? Object.values(raw).join(", ")
          : "Connection failed. Please check your credentials.";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await axiosPrivate.get("/api/auth/v1/storage-config");
        if (res.data) {
          setForm((prev) => ({ ...prev, ...res.data }));
          if (res.data.azure_connection_string) {
            fetchAzureContainers(res.data.azure_connection_string);
          }
        }
      } catch (err) {
        console.error(err);
      }
    };

    loadConfig();
  }, []);

  const fetchDistribution = async () => {
    try {
      const res = await axiosPrivate.get("/api/storage/distribution");
      setDistribution(res.data);
    } catch (err) {
      console.error("Failed to fetch storage distribution", err);
    }
  };

  const handleScan = async () => {
    setScanning(true);
    try {
      await axiosPrivate.post("/api/storage/distribution/scan");
      setSnackbar({ open: true, message: "Scan started in background", severity: "info" });
    } catch {
      setSnackbar({ open: true, message: "Failed to trigger scan", severity: "error" });
    } finally {
      setTimeout(() => setScanning(false), 2000);
    }
  };

  const startMigration = async () => {
    try {
      await axiosPrivate.post("/api/storage/migration/start", { delete_source: deleteSource });
      setSnackbar({ open: true, message: "Migration started", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || "Failed to start migration", severity: "error" });
    }
  };

  useEffect(() => {
    fetchDistribution();
    const interval = setInterval(async () => {
      try {
        const res = await axiosPrivate.get("/api/storage/migration/status");
        setMigrationStatus(res.data);
        if (res.data.status === "idle" || res.data.status === "completed" || res.data.status === "failed") {
          fetchDistribution();
        }
      } catch (err) {
        console.error(err);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getProviderLabel = (name) => {
    const labels = { local: "Local Disk", azure: "Azure Blob", s3: "AWS S3" };
    return labels[name] || name;
  };

  return (
    <>
      <div style={{ padding: "8px 4px" }}>
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e5e7eb",
            borderRadius: "14px",
            padding: "32px",
            background: "#fff",
            margin: "0 32px 0 0",
          }}
        >
          <div style={{ marginBottom: "28px" }}>
            <p
              style={{
                marginTop: "6px",
                fontSize: "14px",
                color: "#6b7280",
              }}
            >
              Configure the storage backend for images uploaded by edge devices.
            </p>
          </div>

          {/* Storage Distribution Section */}
          <Box sx={{ mb: 4, p: 2, bgcolor: "#f9fafb", borderRadius: "10px", border: "1px solid #f3f4f6" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
                <StorageIcon size="small" /> Storage Distribution
              </Typography>
              <Tooltip title="Scan Storage">
                <IconButton onClick={handleScan} disabled={scanning || migrationStatus.status !== "idle"}>
                  <RefreshIcon className={scanning ? "spin-animation" : ""} />
                </IconButton>
              </Tooltip>
            </Box>

            <Grid container spacing={2}>
              {distribution.length > 0 ? (
                distribution.map((item) => (
                  <Grid item xs={12} md={4} key={item.provider_name}>
                    <Card variant="outlined" sx={{ borderRadius: "8px", borderStyle: item.provider_name === form.provider ? "solid" : "dashed" }}>
                      <CardContent sx={{ p: "16px !important" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: item.provider_name === form.provider ? "#49758d" : "#6b7280" }}>
                            {getProviderLabel(item.provider_name)}
                            {item.provider_name === form.provider && " (Active)"}
                          </Typography>
                          {item.provider_name !== form.provider && item.file_count > 0 && (
                            <Tooltip title="Legacy data detected in inactive storage">
                              <WarningIcon sx={{ fontSize: 16, color: "#f59e0b" }} />
                            </Tooltip>
                          )}
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{item.file_count} files</Typography>
                        <Typography variant="caption" color="text.secondary">{formatBytes(item.total_size_bytes)}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                    No storage statistics available. Click the refresh icon to scan.
                  </Typography>
                </Grid>
              )}
            </Grid>

            {/* Distribution Visual Bar */}
            {distribution.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="caption" sx={{ mb: 1, display: "block", color: "#6b7280" }}>Capacity Distribution</Typography>
                <Box sx={{ height: 8, display: "flex", borderRadius: 4, overflow: "hidden", bgcolor: "#e5e7eb" }}>
                  {distribution.map((item, index) => {
                    const totalFiles = distribution.reduce((acc, curr) => acc + curr.file_count, 0);
                    const width = totalFiles > 0 ? (item.file_count / totalFiles) * 100 : 0;
                    const colors = ["#49758d", "#94a3b8", "#cbd5e1"];
                    return width > 0 ? (
                      <Box key={index} sx={{ width: `${width}%`, bgcolor: colors[index % colors.length], height: "100%" }} />
                    ) : null;
                  })}
                </Box>
              </Box>
            )}
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={12}>
              <FormControl fullWidth>
                <InputLabel id="provider-label">Storage Provider</InputLabel>
                <Select
                  labelId="provider-label"
                  value={form.provider}
                  label="Storage Provider"
                  onChange={(e) => handleChange("provider", e.target.value)}
                >
                  <MenuItem value="local">Local Storage</MenuItem>
                  <MenuItem value="s3">AWS S3</MenuItem>
                  <MenuItem value="azure">Azure Blob Storage</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {form.provider === "s3" && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="S3 Bucket"
                    placeholder="my-bucket-name"
                    value={form.s3_bucket || ""}
                    onChange={(e) => handleChange("s3_bucket", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="AWS Region"
                    placeholder="us-east-1"
                    value={form.s3_region || ""}
                    onChange={(e) => handleChange("s3_region", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Access Key ID"
                    value={form.s3_access_key || ""}
                    onChange={(e) => handleChange("s3_access_key", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Secret Access Key"
                    value={form.s3_secret_key || ""}
                    onChange={(e) => handleChange("s3_secret_key", e.target.value)}
                  />
                </Grid>
              </>
            )}

            {form.provider === "azure" && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Azure Connection String"
                    placeholder="DefaultEndpointsProtocol=https;AccountName=...;"
                    value={form.azure_connection_string || ""}
                    onChange={(e) => handleChange("azure_connection_string", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    freeSolo
                    options={azureContainers}
                    loading={fetchingContainers}
                    value={form.azure_container || ""}
                    onInputChange={(event, newInputValue) => {
                      handleChange("azure_container", newInputValue);
                    }}
                    onChange={(event, newValue) => {
                      handleChange("azure_container", newValue || "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        label="Azure Container"
                        placeholder="Select or type container name"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {fetchingContainers ? <CircularProgress color="inherit" size={20} /> : null}
                              <Tooltip title="Fetch available containers from Azure">
                                <IconButton
                                  size="small"
                                  onClick={() => fetchAzureContainers(form.azure_connection_string)}
                                  disabled={fetchingContainers || !form.azure_connection_string}
                                >
                                  <RefreshIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  {form.azure_container && !azureContainers.includes(form.azure_container) && (
                    <Button
                      size="small"
                      onClick={handleCreateContainer}
                      sx={{ mt: 0.5, textTransform: "none", fontSize: "12px", color: "#49758d", fontWeight: 600 }}
                    >
                      + Create container '{form.azure_container}' in Azure
                    </Button>
                  )}
                </Grid>
              </>
            )}
          </Grid>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "32px",
              gap: "16px",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleTestConnection}
              disabled={testing}
              startIcon={testing ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                paddingX: "26px",
                fontWeight: 600,
                color: "#49758d",
                borderColor: "#49758d",
                "&:hover": {
                  borderColor: "#3f667a",
                  backgroundColor: "rgba(73, 117, 141, 0.04)",
                },
              }}
            >
              {testing ? "Testing..." : "Test Connection"}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                backgroundColor: "#49758d",
                borderRadius: "10px",
                textTransform: "none",
                paddingX: "26px",
                fontWeight: 600,
                boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                "&:hover": {
                  backgroundColor: "#3f667a",
                },
              }}
            >
              Save Configuration
            </Button>
          </div>

          {/* Migration Tool Section */}
          <Divider sx={{ my: 4 }} />
          <Box sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: "10px" }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
              <CloudQueueIcon size="small" /> Migration Manager
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Transfer your images between storage locations. Useful when switching providers or clearing server disk space.
            </Typography>

            {migrationStatus.status !== "idle" ? (
              <Box sx={{ py: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {migrationStatus.status === "migrating" ? "Migration in Progress..." : 
                     migrationStatus.status === "scanning" ? "Scanning Storage..." : "Processing..."}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{migrationStatus.progress}%</Typography>
                </Box>
                <LinearProgress variant="determinate" value={migrationStatus.progress} sx={{ height: 8, borderRadius: 4, mb: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontStyle: "italic" }}>
                  Currently processing: {migrationStatus.current_file || "Scanning filesystem..."}
                </Typography>
                
                {migrationStatus.errors?.length > 0 && (
                  <Box sx={{ mt: 2, p: 1, bgcolor: "#fff1f2", borderRadius: "4px", border: "1px solid #fecaca" }}>
                    <Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>Recent Errors:</Typography>
                    {migrationStatus.errors.slice(-2).map((err, i) => (
                      <Typography key={i} variant="caption" sx={{ display: "block", color: "#b91c1c" }}>• {err}</Typography>
                    ))}
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <FormControlLabel
                  control={<Checkbox checked={deleteSource} onChange={(e) => setDeleteSource(e.target.checked)} />}
                  label={<Typography variant="body2">Delete source files after successful transfer (DANGEROUS)</Typography>}
                />
                
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button 
                    variant="contained" 
                    onClick={startMigration}
                    disabled={form.provider === "local" && distribution.every(d => d.provider_name === 'local' || d.file_count === 0)}
                    sx={{ 
                      textTransform: "none", bgcolor: "#49758d", borderRadius: "8px", px: 4,
                      "&:hover": { bgcolor: "#3f667a" }
                    }}
                  >
                    Start Migration
                  </Button>
                  <Tooltip title="Migration direction is automatically determined based on your active provider setting.">
                    <IconButton size="small"><WarningIcon sx={{ fontSize: 18, color: "#9ca3af" }} /></IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: "10px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default StorageConfiguration;
